import { useState, type FormEvent } from "react";

import { refusalText } from "../../../api/errors";
import { useApiCommand, useApiQuery } from "../../../api/hooks";
import type { WorkspaceAdminView } from "../../../api/types";
import { Button } from "../../../ui/Button";
import { Card } from "../../../ui/Card";
import { ConfirmDialog } from "../../../ui/ConfirmDialog";
import { Dialog } from "../../../ui/Dialog";
import { EmptyState, Loading } from "../../../ui/EmptyState";
import { Pill } from "../../../ui/Pill";
import { TextField } from "../../../ui/TextField";
import { showToast } from "../../../ui/toastStore";
import { formatWhen, plural } from "../shared/format";
import { checkNewDiscipline } from "./accountChecks";
import { CheckList } from "./CheckList";
import { useWorkspaceCache } from "./useAdminPeople";

function AddDisciplineDialog({ existing, onClose }: { existing: readonly WorkspaceAdminView[]; onClose: () => void }) {
  const add = useApiCommand("add_workspace");
  const cache = useWorkspaceCache();
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const result = checkNewDiscipline(code, name, existing.map((workspace) => workspace.code));

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!result.ok) return;
    setProblem(null);
    try {
      const reply = await add.mutateAsync({ code: code.trim(), display_name: name.trim() });
      cache.replace(reply.data);
      showToast({ text: `The discipline ${reply.data.display_name} is added, with its own space and the starting pack loaded.` });
      onClose();
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <Dialog
      open
      closeOnBackdrop={false}
      title="Add a discipline"
      onClose={add.isPending ? () => undefined : onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={add.isPending}>
            Cancel
          </Button>
          <Button variant="primary" busy={add.isPending} disabled={!result.ok} onClick={() => void submit()}>
            Add the discipline
          </Button>
        </>
      }
    >
      <form className="stack" onSubmit={(event) => void submit(event)} autoComplete="off">
        <p className="muted">The app makes the discipline's own space and loads the starting pack into it. You add no ontology content: the discipline's teams do.</p>
        <TextField label="Code" hint="What the discipline is known by. It cannot be changed later." value={code} onChange={(event) => setCode(event.target.value)} autoComplete="off" data-autofocus />
        <TextField label="Name" value={name} onChange={(event) => setName(event.target.value)} autoComplete="off" />
        <CheckList checks={result.checks} started={code.length > 0 || name.length > 0} />
        {problem && (
          <p role="alert" className="form-error">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}

function DisciplineRow({ workspace }: { workspace: WorkspaceAdminView }) {
  const cache = useWorkspaceCache();
  const close = useApiCommand("close_workspace");
  const exportIt = useApiCommand("export_workspace");
  const [confirmClose, setConfirmClose] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const closed = workspace.status === "closed";

  const doClose = async () => {
    setProblem(null);
    try {
      const reply = await close.mutateAsync({ code: workspace.code });
      cache.replace(reply.data);
      setConfirmClose(false);
      showToast({ text: `${workspace.display_name} is closed. Its work stays and can be read.` });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  const doExport = async () => {
    setProblem(null);
    try {
      const reply = await exportIt.mutateAsync({ code: workspace.code });
      cache.replace(reply.data);
      showToast({ text: `${workspace.display_name} is exported.` });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <li className="ra-discipline" data-status={workspace.status} data-code={workspace.code}>
      <div className="ra-discipline__head row">
        <strong>{workspace.display_name}</strong> <code>{workspace.code}</code>
        <Pill>{closed ? "Closed" : "Open"}</Pill>
        <span className="muted">{plural(workspace.members, "member")}</span>
      </div>
      <p className="muted">
        Added {formatWhen(workspace.created_at)}.{closed && workspace.closed_at ? ` Closed ${formatWhen(workspace.closed_at)}.` : ""}
      </p>
      {workspace.exports.length > 0 && (
        <p className="ra-discipline__exports">
          <span className="muted">Exports:</span> {workspace.exports.join(", ")}
        </p>
      )}
      <div className="row">
        {!closed && (
          <Button onClick={() => setConfirmClose(true)} aria-label={`Close the discipline ${workspace.display_name}`}>
            Close it
          </Button>
        )}
        {closed && (
          <Button busy={exportIt.isPending} onClick={() => void doExport()} aria-label={`Export the closed discipline ${workspace.display_name}`}>
            Export it
          </Button>
        )}
      </div>
      {problem && !confirmClose && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}
      <ConfirmDialog
        open={confirmClose}
        title={`Close ${workspace.display_name}?`}
        message="Its work stays and can be read, but nobody can change it any more, and it cannot be opened again from this page. Nothing is deleted. A closed discipline can be exported."
        confirmLabel="Close the discipline"
        cancelLabel="Keep it open"
        busy={close.isPending}
        error={problem}
        onConfirm={() => void doClose()}
        onCancel={() => setConfirmClose(false)}
      />
    </li>
  );
}

/** The disciplines: add one, close one (its work stays, read-only; nothing is deleted) and export a closed one (ui-direction screen 10). */
export function DisciplinesSection({ pointed }: { pointed: boolean }) {
  const query = useApiQuery("admin_workspaces", {});
  const [adding, setAdding] = useState(false);
  const list = query.data ?? [];
  return (
    <Card className="ra-section" id="disciplines" aria-labelledby="ra-disciplines-title" data-pointed={pointed ? "true" : undefined}>
      <div className="ra-section__head row">
        <h2 id="ra-disciplines-title">Disciplines</h2>
        {query.data && <span className="muted">{plural(list.length, "discipline")}</span>}
        <Button variant="primary" onClick={() => setAdding(true)}>
          Add a discipline
        </Button>
      </div>
      <p className="muted">
        The admin adds, closes and exports disciplines, and adds no ontology content. The export of a closed discipline is a backup kept on the server; this page shows only its name, never what is inside.
      </p>
      {query.isPending && <Loading>Reading the disciplines</Loading>}
      {query.isError && (
        <p role="alert" className="form-error">
          {refusalText(query.error)} <Button onClick={() => void query.refetch()}>Try again</Button>
        </p>
      )}
      {query.data && list.length === 0 && (
        <EmptyState title="No discipline yet" action={<Button onClick={() => setAdding(true)}>Add the first discipline</Button>}>
          Each discipline gets its own space. People work in a discipline once you add them to it.
        </EmptyState>
      )}
      {list.length > 0 && (
        <ul className="ra-disciplines" aria-label="Disciplines">
          {list.map((workspace) => (
            <DisciplineRow key={workspace.code} workspace={workspace} />
          ))}
        </ul>
      )}
      {adding && <AddDisciplineDialog existing={list} onClose={() => setAdding(false)} />}
    </Card>
  );
}
